import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CachedIcon from '@mui/icons-material/Cached';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import { useHasActivityBadges } from 'src/components/ActivityControls';
import { fetchActivityCounts } from 'src/actions/activityActions';

const useStyles = makeStyles()((theme) => ({
  refreshButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      fontSize: '1.1rem',
    },
  },
}));

function TableRefresh({
  onRefresh,
  refreshRateLimitMs = null,
  loading = false,
  tooltipText = 'Refresh data',
}) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const hasActivityBadges = useHasActivityBadges();
  const [isRateLimited, setIsRateLimited] = useState(false);

  const isDisabled = loading || isRateLimited;

  const handleRefresh = useCallback(() => {
    if (isRateLimited || loading) return;

    onRefresh();
    // CON-2036 review — a refresh also re-reads the new-activity numbers
    // (sidebar, Unseen chips, tab badges), which otherwise move only by
    // socket. ACTIVITY_BADGES holders only; the CP app's refresh does the same.
    if (hasActivityBadges) dispatch(fetchActivityCounts());
    if (refreshRateLimitMs) {
      setIsRateLimited(true);
      setTimeout(() => setIsRateLimited(false), refreshRateLimitMs);
    }
  }, [onRefresh, refreshRateLimitMs, isRateLimited, loading, hasActivityBadges, dispatch]);

  const getTooltipText = () => {
    if (loading) {
      return 'Refreshing...';
    }
    if (isRateLimited) {
      return 'Please wait a moment before refreshing again';
    }
    return tooltipText;
  };

  return (
    <TooltipMUI6
      title={getTooltipText()}
    >
      <span>
        <IconButton
          className={classes.refreshButton}
          onClick={handleRefresh}
          disabled={isDisabled}
          size="small"
        >
          <CachedIcon />
        </IconButton>
      </span>
    </TooltipMUI6>
  );
}

TableRefresh.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  tooltipText: PropTypes.string,
  refreshRateLimitMs: PropTypes.number,
};

export default TableRefresh;
